/**
 * CodexSystem — listens on 'codex:unlocked', dedupes, persists to
 * metaProgress, and fires a non-intrusive notification.
 *
 * Also exposes getEntries() grouped by category for the CodexMenu UI.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import type { CodexEntry } from '@/data/types';

export class CodexSystem {
  private defs: Record<string, CodexEntry>;
  private onNotify: ((entry: CodexEntry) => void) | null = null;

  constructor(defs: Record<string, CodexEntry>) {
    this.defs = defs;
    bus.on('codex:unlocked', ({ entryId }) => this.unlock(entryId));
  }

  setNotifier(fn: (entry: CodexEntry) => void): void { this.onNotify = fn; }

  unlock(id: string): void {
    const meta = saveManager.getMeta();
    if (meta.codexUnlocked.includes(id)) return;
    const def = this.defs[id];
    if (!def) return;
    meta.codexUnlocked.push(id);
    saveManager.getRun().codexUnlockedThisRun.push(id);
    bus.emit('audio:cue', { id: 'codex', volume: 0.25 });
    this.onNotify?.(def);
  }

  isUnlocked(id: string): boolean {
    return saveManager.getMeta().codexUnlocked.includes(id);
  }

  getEntry(id: string): CodexEntry | undefined { return this.defs[id]; }

  entriesByCategory(): Record<string, CodexEntry[]> {
    const out: Record<string, CodexEntry[]> = {
      concept: [], food: [], mutation: [], predator: [], event: [],
    };
    for (const def of Object.values(this.defs)) {
      (out[def.category] ??= []).push(def);
    }
    for (const k of Object.keys(out)) out[k].sort((a, b) => a.title.localeCompare(b.title));
    return out;
  }
}
